import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import theme from "../../theme/theme";
import { Api } from "../../network/api";

const ResultDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [isEditMode, setIsEditMode] = useState(false);
  const [editableData, setEditableData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  const getScoreColor = (percentage) => {
    if (percentage >= 90)
      return { color: "#059669", bg: "#d1fae5", text: "Excellent" };
    if (percentage >= 80)
      return { color: "#10b981", bg: "#d1fae5", text: "Good" };
    if (percentage >= 70)
      return { color: "#f59e0b", bg: "#fef3c7", text: "Satisfactory" };
    if (percentage >= 60)
      return { color: "#f97316", bg: "#fed7aa", text: "Needs Improvement" };
    if (percentage >= 50)
      return { color: "#ef4444", bg: "#fecaca", text: "Poor" };
    return { color: "#dc2626", bg: "#fecaca", text: "Failing" };
  };

  const createScorecardFromIRAC = (data) => {
    if (!data) return {};
  
    try {
      if (data.scorecard) {
        if (typeof data.scorecard === "object" && data.scorecard !== null) {
          return data.scorecard;
        }
        if (typeof data.scorecard === "string") {
          try {
            // Fix single quotes before parsing
            const fixed = data.scorecard.replace(/'/g, '"');
            const parsed = JSON.parse(fixed);
            return typeof parsed === "object" && parsed !== null ? parsed : {};
          } catch (err) {
            console.warn("Invalid scorecard format:", err, data.scorecard);
            return {};
          }
        }
      }
    } catch (error) {
      console.warn("Error parsing scorecard from backend:", error);
    }
  
    return {
      "Issue Spotting": {
        total_marks: data.irac_issue_spotting_total || 0,
        obtained_marks: data.irac_issue_spotting_obtained || 0,
      },
      "Rule Statements": {
        total_marks: data.irac_rule_statements_total || 0,
        obtained_marks: data.irac_rule_statements_obtained || 0,
      },
      "Analysis Depth": {
        total_marks: data.irac_analysis_depth_total || 0,
        obtained_marks: data.irac_analysis_depth_obtained || 0,
      },
      "Conclusion": {
        total_marks: data.irac_conclusion_accuracy_total || 0,
        obtained_marks: data.irac_conclusion_accuracy_obtained || 0,
      },
      "Organization": {
        total_marks: data.irac_organization_total || 0,
        obtained_marks: data.irac_organization_obtained || 0,
      },
      "Writing Style": {
        total_marks: data.irac_writing_style_total || 0,
        obtained_marks: data.irac_writing_style_obtained || 0,
      },
    };
  };

  const IRAC_COMPONENTS = ["Issue", "Rule", "Analysis", "Conclusion", "Organization", "Writing Style"];

  const createIRACComponent = (apiData, prefix) => {
    try {
      return {
        obtained_marks: Number(apiData[`${prefix}_obtained`]) || 0,
        total_marks: Number(apiData[`${prefix}_total`]) || 0,
        feedback: ""
      };
    } catch (error) {
      console.warn(`Error creating IRAC component for ${prefix}:`, error);
      return { obtained_marks: 0, total_marks: 0, feedback: "" };
    }
  };

  const calculateIssueTotals = (issueData) => {
    try {
      if (!issueData || typeof issueData !== 'object') {
        return { issueTotal: 0, maxIssueScore: 0, percentage: 0 };
      }

      const issueTotal = IRAC_COMPONENTS.reduce(
        (sum, comp) => {
          const componentData = issueData?.[comp];
          return sum + (Number(componentData?.obtained_marks) || 0);
        },
        0
      );

      const maxIssueScore = IRAC_COMPONENTS.reduce(
        (sum, comp) => {
          const componentData = issueData?.[comp];
          return sum + (Number(componentData?.total_marks) || 0);
        },
        0
      );

      return {
        issueTotal,
        maxIssueScore,
        percentage: maxIssueScore > 0 ? (issueTotal / maxIssueScore) * 100 : 0
      };
    } catch (error) {
      console.error("Error calculating issue totals:", error);
      return { issueTotal: 0, maxIssueScore: 0, percentage: 0 };
    }
  };

  const updateIRACData = (callback) => {
    if (!editableData?.irac_breakdown?.issues) {
      console.warn("Invalid IRAC data structure");
      return editableData;
    }
    try {
      return callback();
    } catch (error) {
      console.error("Error updating IRAC data:", error);
      return editableData;
    }
  };

  const restructureIRACBreakdown = (apiData) => {
    try {
      if (!apiData || typeof apiData !== 'object') {
        console.warn("Invalid apiData provided to restructureIRACBreakdown");
        return apiData;
      }
      const hasIRACData = apiData?.irac_issue_spotting_obtained !== undefined;
      const needsRestructure = !apiData.irac_breakdown || 
                               !apiData.irac_breakdown.issues || 
                               Object.keys(apiData.irac_breakdown.issues).length === 0;

      if (!hasIRACData || !needsRestructure) {
        return apiData;
      }
      const newIRACBreakdown = {
        issues: {
          "issue_1": {
            issue_name: "Primary Issue",
            Issue: createIRACComponent(apiData, "irac_issue_spotting"),
            Rule: createIRACComponent(apiData, "irac_rule_statements"),
            Analysis: createIRACComponent(apiData, "irac_analysis_depth"),
            Conclusion: createIRACComponent(apiData, "irac_conclusion_accuracy"),
            Organization: createIRACComponent(apiData, "irac_organization"),
            "Writing Style": createIRACComponent(apiData, "irac_writing_style")
          }
        }
      };

      const issueData = newIRACBreakdown.issues["issue_1"];
      const totals = calculateIssueTotals(issueData);

      issueData.issue_total_score = totals.issueTotal;
      issueData.issue_percentage = totals.percentage;

      return {
        ...apiData,
        irac_breakdown: newIRACBreakdown
      };
    } catch (error) {
      console.error("Error restructuring IRAC breakdown:", error);
      return apiData;
    }
  };

  useEffect(() => {
    const fetchResultDetails = async () => {
      if (!id) {
        console.warn("No ID provided for fetching result details");
        return;
      }

      setLoading(true);
      
      try {
        const response = await Api.getResultDetails(id);
        
        if (!response?.data) {
          throw new Error("Invalid API response structure");
        }

        console.log("API Response:", response.data);
        const navigationState = location.state;
        
        setResult({
          id: response.data.id,
          userName:
            navigationState?.userName ||
            response.data.user_name ||
            response.data.user?.name ||
            "Unknown User",
          assignmentName:
            navigationState?.assignmentName ||
            `${response.data.subject} Essay Analysis`,
          submissionDate:
            navigationState?.submissionDate ||
            new Date(response.data.created_at).toLocaleDateString(),
          status: navigationState?.status || response.data.status,
          isFlagged: navigationState?.isFlagged || response.data.flagged,
        });

        const apiData = JSON.parse(JSON.stringify(response.data));
        
        const processedData = restructureIRACBreakdown(apiData);
        
        setEditableData(processedData);
        setOriginalData(processedData);
        
      } catch (error) {
        console.error("Error loading result details:", error);
        alert("Failed to load result details. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchResultDetails();
  }, [id, location.state]);

  const calculatePercentage = (scores) => {
    let total = 0;
    let maxPossible = 0;

    Object?.values(scores)?.forEach((scoreObj) => {
      if (
        scoreObj &&
        typeof scoreObj === "object" &&
        scoreObj.total_marks &&
        scoreObj.obtained_marks
      ) {
        total += scoreObj.obtained_marks || 0;
        maxPossible += scoreObj.total_marks || 0;
      }
    });

    return maxPossible > 0 ? ((total / maxPossible) * 100).toFixed(2) : 0;
  };

  const calculateTotalPossibleScore = (data) => {
    const scorecard = createScorecardFromIRAC(data);
    return Object?.values(scorecard)?.reduce((sum, scoreObj) => {
      if (scoreObj && typeof scoreObj === "object" && scoreObj.total_marks) {
        return sum + (scoreObj.total_marks || 0);
      }
      return sum;
    }, 0);
  };

  const handleScoreChange = (category, newScore) => {
    if (!editableData) {
      console.warn("Cannot change score: editable data not available");
      return;
    }

    try {
      const currentScorecard = createScorecardFromIRAC(editableData);
      const currentCategory = currentScorecard[category];
      
      if (!currentCategory) {
        console.warn(`Category "${category}" not found in scorecard`);
        return;
      }

      const newScoreNum = Math.max(0, parseInt(newScore) || 0);
      const maxScore = currentCategory.total_marks || 0;
      const adjustedScore = Math.min(newScoreNum, maxScore);

      const updatedData = { ...editableData };
      if (!updatedData.scorecard) {
        updatedData.scorecard = currentScorecard;
      }

      let scorecardData = updatedData.scorecard;
      if (typeof scorecardData === "string") {
        try {
          scorecardData = JSON.parse(scorecardData);
        } catch (error) {
          console.warn("Error parsing scorecard:", error);
          scorecardData = currentScorecard;
        }
      }

      scorecardData[category] = {
        ...scorecardData[category],
        obtained_marks: adjustedScore,
      };

      updatedData.scorecard = scorecardData;

      const totalScore = Object?.values(scorecardData)?.reduce((sum, scoreObj) => {
        if (scoreObj && typeof scoreObj === "object" && scoreObj.obtained_marks !== undefined) {
          return sum + (Number(scoreObj.obtained_marks) || 0);
        }
        return sum;
      }, 0);

      const maxPossibleScore = Object?.values(scorecardData)?.reduce(
        (sum, scoreObj) => {
          if (scoreObj && typeof scoreObj === "object" && scoreObj.total_marks !== undefined) {
            return sum + (Number(scoreObj.total_marks) || 0);
          }
          return sum;
        },
        0
      );

      updatedData.score = totalScore.toString();
      updatedData.total_score = maxPossibleScore.toString();
      updatedData.max_possible_score = maxPossibleScore;
      updatedData.percentage_score = `${parseFloat(
        calculatePercentage(scorecardData)
      )}%`;
      updatedData.bar_equivalent_score = totalScore.toString();

      setEditableData(updatedData);
    } catch (error) {
      console.error("Error handling score change:", error);
    }
  };

  const handleMaxScoreChange = (category, newMaxScore) => {
    if (!editableData) {
      console.warn("Cannot change max score: editable data not available");
      return;
    }

    try {
      const currentScorecard = createScorecardFromIRAC(editableData);
      const currentCategory = currentScorecard[category];
      
      if (!currentCategory) {
        console.warn(`Category "${category}" not found in scorecard`);
        return;
      }

      const newMaxScoreNum = Math.max(0, parseInt(newMaxScore) || 0);
      const currentObtainedScore = currentCategory.obtained_marks || 0;

      const adjustedObtainedScore = Math.min(
        currentObtainedScore,
        newMaxScoreNum
      );

      const updatedData = { ...editableData };

      if (!updatedData.scorecard) {
        updatedData.scorecard = currentScorecard;
      }

      let scorecardData = updatedData.scorecard;
      if (typeof scorecardData === "string") {
        try {
          scorecardData = JSON.parse(scorecardData);
        } catch (error) {
          console.warn("Error parsing scorecard:", error);
          scorecardData = currentScorecard;
        }
      }

      scorecardData[category] = {
        ...scorecardData[category],
        total_marks: newMaxScoreNum,
        obtained_marks: adjustedObtainedScore,
      };

      updatedData.scorecard = scorecardData;

      const totalScore = Object?.values(scorecardData)?.reduce((sum, scoreObj) => {
        if (scoreObj && typeof scoreObj === "object" && scoreObj.obtained_marks !== undefined) {
          return sum + (Number(scoreObj.obtained_marks) || 0);
        }
        return sum;
      }, 0);

      const maxPossibleScore = Object?.values(scorecardData)?.reduce(
        (sum, scoreObj) => {
          if (scoreObj && typeof scoreObj === "object" && scoreObj.total_marks !== undefined) {
            return sum + (Number(scoreObj.total_marks) || 0);
          }
          return sum;
        },
        0
      );

      updatedData.score = totalScore.toString();
      updatedData.total_score = maxPossibleScore.toString();
      updatedData.max_possible_score = maxPossibleScore;
      updatedData.percentage_score = `${parseFloat(
        calculatePercentage(scorecardData)
      )}%`;
      updatedData.bar_equivalent_score = totalScore.toString();

      setEditableData(updatedData);
    } catch (error) {
      console.error("Error handling max score change:", error);
    }
  };

  const handleFeedbackChange = (category, newFeedback) => {
    if (!editableData) return;

    setEditableData({
      ...editableData,
      feedback: {
        ...(editableData.feedback || {}),
        [category]: newFeedback,
      },
    });
  };

  const handleRewriteExampleChange = (category, newExample) => {
    if (!editableData) return;

    setEditableData({
      ...editableData,
      re_write_example: {
        ...(editableData.re_write_example || {}),
        [category]: newExample,
      },
    });
  };

  const handleGradeSummaryChange = (newSummary) => {
    if (!editableData) return;

    setEditableData({
      ...editableData,
      grade_summary: newSummary,
    });
  };

  const handleMissedIssuesChange = (newIssues) => {
    if (!editableData) return;

    setEditableData({
      ...editableData,
      missed_issues: newIssues
        .split("\n")
        .filter((issue) => issue.trim() !== ""),
    });
  };

  const handleStatusChange = (newStatus) => {
    if (!editableData) return;

    setEditableData({
      ...editableData,
      status: newStatus,
    });
  };

  const handleAnalysisChange = (category, newAnalysis) => {
    if (!editableData) return;

    setEditableData({
      ...editableData,
      analysis: {
        ...(editableData.analysis || {}),
        [category]: newAnalysis,
      },
    });
  };

  const handleMissingTopicsChange = (field, newValue) => {
    if (!editableData) return;

    setEditableData({
      ...editableData,
      missing_topics_analysis: {
        ...editableData.missing_topics_analysis,
        [field]: newValue.split("\n").filter((item) => item.trim() !== ""),
      },
    });
  };

  const handleIRACScoreChange = (issueKey, component, newScore) => {
    if (!editableData?.irac_breakdown?.issues?.[issueKey]?.[component]) {
      console.error("Invalid IRAC data structure for score change");
      return;
    }

    try {
      const newScoreNum = Math.max(0, parseInt(newScore) || 0);
      const maxScore = editableData.irac_breakdown.issues[issueKey][component].total_marks || 0;
      const adjustedScore = Math.min(newScoreNum, maxScore);

      const updatedData = {
        ...editableData,
        irac_breakdown: {
          ...editableData.irac_breakdown,
          issues: {
            ...editableData.irac_breakdown.issues,
            [issueKey]: {
              ...editableData.irac_breakdown.issues[issueKey],
              [component]: {
                ...editableData.irac_breakdown.issues[issueKey][component],
                obtained_marks: adjustedScore,
              },
            },
          },
        },
      };

      // Calculate totals using helper function
      const issueData = updatedData.irac_breakdown.issues[issueKey];
      const totals = calculateIssueTotals(issueData);

      if (issueData) {
        issueData.issue_total_score = totals.issueTotal;
        issueData.issue_percentage = totals.percentage;
      }

      setEditableData(updatedData);
    } catch (error) {
      console.error("Error handling IRAC score change:", error);
      // Optionally: show user-friendly error message
    }
  };

  const handleIRACMaxScoreChange = (issueKey, component, newMaxScore) => {
    if (!editableData?.irac_breakdown?.issues?.[issueKey]?.[component]) {
      console.error("Invalid IRAC data structure for max score change");
      return;
    }

    try {
      const newMaxScoreNum = Math.max(0, parseInt(newMaxScore) || 0);
      const currentObtainedScore =
        editableData.irac_breakdown.issues[issueKey][component].obtained_marks || 0;

      const adjustedObtainedScore = Math.min(currentObtainedScore, newMaxScoreNum);

      const updatedData = {
        ...editableData,
        irac_breakdown: {
          ...editableData.irac_breakdown,
          issues: {
            ...editableData.irac_breakdown.issues,
            [issueKey]: {
              ...editableData.irac_breakdown.issues[issueKey],
              [component]: {
                ...editableData.irac_breakdown.issues[issueKey][component],
                total_marks: newMaxScoreNum,
                obtained_marks: adjustedObtainedScore,
              },
            },
          },
        },
      };

      // Calculate totals using helper function
      const issueData = updatedData.irac_breakdown.issues[issueKey];
      const totals = calculateIssueTotals(issueData);

      if (issueData) {
        issueData.issue_total_score = totals.issueTotal;
        issueData.issue_percentage = totals.percentage;
      }

      setEditableData(updatedData);
    } catch (error) {
      console.error("Error handling IRAC max score change:", error);
      // Optionally: show user-friendly error message
    }
  };

  const handleIRACFeedbackChange = (issueKey, component, newFeedback) => {
    if (!editableData?.irac_breakdown?.issues?.[issueKey]?.[component]) {
      console.error("Invalid IRAC data structure for feedback change");
      return;
    }

    try {
      setEditableData({
        ...editableData,
        irac_breakdown: {
          ...editableData.irac_breakdown,
          issues: {
            ...editableData.irac_breakdown.issues,
            [issueKey]: {
              ...editableData.irac_breakdown.issues[issueKey],
              [component]: {
                ...editableData.irac_breakdown.issues[issueKey][component],
                feedback: newFeedback || "",
              },
            },
          },
        },
      });
    } catch (error) {
      console.error("Error handling IRAC feedback change:", error);
    }
  };

  const handleBasicFieldChange = (field, newValue) => {
    if (!editableData) return;

    setEditableData({
      ...editableData,
      [field]: newValue,
    });
  };

  const handleSave = async () => {
    if (!editableData || !id) {
      console.error("Cannot save: missing editable data or ID");
      alert("Cannot save: missing required data");
      return;
    }

    setSaving(true);
    try {
      const currentScorecard = createScorecardFromIRAC(editableData);

      if (!currentScorecard || Object.keys(currentScorecard).length === 0) {
        console.warn("Invalid or empty scorecard generated");
      }

      const obtainedScore = Object?.values(currentScorecard)?.reduce(
        (sum, scoreObj) => {
          if (
            scoreObj &&
            typeof scoreObj === "object" &&
            scoreObj.obtained_marks !== undefined
          ) {
            return sum + (Number(scoreObj.obtained_marks) || 0);
          }
          return sum;
        },
        0
      );

      const totalPossibleScore =
        editableData.total_possible_score ||
        Object?.values(currentScorecard)?.reduce((sum, scoreObj) => {
          if (
            scoreObj &&
            typeof scoreObj === "object" &&
            scoreObj.total_marks !== undefined
          ) {
            return sum + (Number(scoreObj.total_marks) || 0);
          }
          return sum;
        }, 0);

      const percentage =
        totalPossibleScore > 0 ? ((obtainedScore / totalPossibleScore) * 100) : 0;

      let scorecardString;
      try {
        scorecardString = JSON.stringify(currentScorecard);
      } catch (error) {
        console.error("Error stringifying scorecard:", error);
        scorecardString = "{}";
      }

      const payload = {
        score: obtainedScore.toString(),
        scorecard: scorecardString,
        total_possible_score: totalPossibleScore.toString(),
        percentage: Math.round(percentage * 100) / 100,
        bar_equivalent_score: obtainedScore.toString(),
        feedback: editableData.feedback || {},
        analysis: editableData.analysis || {},
        re_write_example: editableData.re_write_example || {},
        missed_issues: editableData.missed_issues || {},
        irac_breakdown: editableData.irac_breakdown || {},
        paragraph_comparison: editableData?.paragraph_comparison || {},
        paragraph_analysis: editableData?.paragraph_analysis || {},
        missing_topics_analysis: editableData.missing_topics_analysis || {},
        grade_summary: editableData.grade_summary || {},
        essay_prompt_info: editableData.essay_prompt_info || {},
        subject: editableData.subject || "",
        reason: "Admin override",
      };

      console.log("Saving with payload:", payload);
      console.log("Scorecard being sent:", currentScorecard);
      console.log(
        "Calculated scores - Obtained:",
        obtainedScore,
        "Total Possible:",
        totalPossibleScore,
        "Percentage:",
        percentage
      );

      const response = await Api.updateAIResult(id, payload);
      
      if (response?.data) {
        console.log("Update successful:", response.data);
        setOriginalData(JSON.parse(JSON.stringify(editableData)));
        setIsEditMode(false);
        alert("Result updated successfully!");
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Error updating result:", error);
      const errorMessage = error?.response?.data?.message || error?.message || "Unknown error";
      alert(`Failed to update result: ${errorMessage}. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (originalData) {
      setEditableData(JSON.parse(JSON.stringify(originalData)));
    }
    setIsEditMode(false);
  };

  const handleBack = () => {
    navigate("/submissions");
  };

  const displayData = editableData;

  if (loading || !displayData) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading analysis details...</p>
          </div>
        </div>
      </div>
      
    );
    
  }

  const scorecardCategories = Object.keys(
    createScorecardFromIRAC(editableData) || {}
  );

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={handleBack}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {result?.assignmentName || "Essay Analysis"}
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  {/* Student: {result?.userName} */}
                  Essay Submitted:{" "}
                  {result?.submissionDate}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-gray-700">
                    Status:
                  </label>

                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      displayData.status === "completed"
                        ? "bg-green-100 text-green-800"
                        : displayData.status === "pending"
                        ? "bg-yellow-100 text-yellow-800"
                        : displayData.status === "flagged"
                        ? "bg-red-100 text-red-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {displayData.status}
                  </span>
                </div>
              </div>

              {!isEditMode ? (
                <button
                  onClick={() => setIsEditMode(true)}
                  className="px-4 py-2 text-white rounded-md transition-colors flex items-center space-x-2"
                  style={{ backgroundColor: theme?.Colors?.primary }}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  <span>Edit</span>
                </button>
              ) : (
                <div className="flex items-center space-x-2">
                  <button
                    onClick={handleCancel}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                    style={{ backgroundColor: theme?.Colors?.primary }}
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Saving...</span>
                      </>
                    ) : (
                      <span>Save</span>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Submission Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm md:text-lg font-bold text-gray-700 mb-1">
                  Subject:
                </label>
                {isEditMode ? (
                  <input
                    type="text"
                    value={displayData.subject || ""}
                    onChange={(e) =>
                      handleBasicFieldChange("subject", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-sm text-gray-900">
                    {displayData.subject || "N/A"}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm md:text-lg font-bold text-gray-700 mb-1">
                  Student Filename:
                </label>
                {isEditMode ? (
                  <input
                    type="text"
                    value={displayData?.original_filename || ""}
                    onChange={(e) =>
                      handleBasicFieldChange(
                        "original_filename",
                        e.target.value
                      )
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                ) : (
                  <p className="text-sm text-gray-900">
                    {displayData?.original_filename || "N/A"}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Overall Performance
                </h3>
              </div>
              <div className="text-center">
                {isEditMode ? (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <label className="text-sm font-medium text-gray-700">
                        Obtained Score:
                      </label>
                      <input
                        type="number"
                        value={displayData?.score || ""}
                        onChange={(e) =>
                          handleBasicFieldChange("score", e.target.value)
                        }
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-center font-bold"
                        placeholder="0"
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <label className="text-sm font-medium text-gray-700">
                        Total Score:
                      </label>
                      <input
                        type="number"
                        value={calculateTotalPossibleScore(displayData)}
                        onChange={(e) =>
                          handleBasicFieldChange("total_score", e.target.value)
                        }
                        className="w-20 px-2 py-1 border border-gray-300 rounded text-center font-bold"
                        placeholder="0"
                      />
                    </div>
                 
                  </div>
                ) : (
                  <>
                    <div
                      className="text-4xl font-bold"
                      style={{
                        color: getScoreColor(displayData?.percentage).color,
                      }}
                    >
                      {displayData?.score || displayData?.total_score}
                    </div>
                    
                  </>
                )}
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Detailed Scorecard
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {scorecardCategories.map((category) => {
                const scorecard = createScorecardFromIRAC(displayData);
                const scoreObj = scorecard[category];
                const score = scoreObj?.obtained_marks || 0;
                const maxScore = scoreObj?.total_marks || 0;
                const percentage = maxScore > 0 ? (score / maxScore) * 100 : 0;
                const colorInfo = getScoreColor(percentage);

                return (
                  <div
                    key={category}
                    className="bg-white border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">{category}</h4>
                      {isEditMode ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="number"
                            min="0"
                            max={maxScore}
                            value={score === 0 ? "" : score}
                            onChange={(e) =>
                              handleScoreChange(category, e.target.value)
                            }
                            onFocus={(e) => {
                              if (e.target.value === "0") {
                                e.target.value = "";
                              }
                            }}
                            onBlur={(e) => {
                              if (e.target.value === "") {
                                e.target.value = "0";
                                handleScoreChange(category, "0");
                              }
                            }}
                            placeholder="0"
                            className="w-16 text-sm font-bold text-gray-700 border border-gray-300 rounded px-2 py-1 text-center"
                          />
                          <span className="text-sm text-gray-500">/</span>
                          <input
                            type="number"
                            min="0"
                            value={maxScore === 0 ? "" : maxScore}
                            onChange={(e) =>
                              handleMaxScoreChange(category, e.target.value)
                            }
                            onFocus={(e) => {
                              if (e.target.value === "0") {
                                e.target.value = "";
                              }
                            }}
                            onBlur={(e) => {
                              if (e.target.value === "") {
                                e.target.value = "0";
                                handleMaxScoreChange(category, "0");
                              }
                            }}
                            placeholder="0"
                            className="w-16 text-sm font-bold text-gray-700 border border-gray-300 rounded px-2 py-1 text-center"
                          />
                        </div>
                      ) : (
                        <div className="text-right">
                          <span
                            className="text-lg font-bold"
                            style={{ color: colorInfo.color }}
                          >
                            {score} / {maxScore}
                          </span>
                          <div
                            className="text-xs font-medium"
                            style={{ color: colorInfo.color }}
                          >
                            {percentage.toFixed(1)}%
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                      <div
                        className="h-3 rounded-full transition-all duration-300"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: colorInfo.color,
                        }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Grade Summary
            </h3>
            {isEditMode ? (
              <textarea
                value={displayData?.grade_summary}
                onChange={(e) => handleGradeSummaryChange(e.target.value)}
                className="w-full text-gray-700 leading-relaxed border border-yellow-300 rounded p-2 bg-white"
                rows="4"
              />
            ) : (
              <p className="text-gray-700 leading-relaxed text-justify">
                {displayData?.grade_summary}
              </p>
            )}
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Detailed Feedback & Analysis
            </h3>
            <div className="space-y-4">
              {scorecardCategories.map((category, index) => (
                <div
                  key={category}
                  className="grid grid-cols-1 lg:grid-cols-3 gap-6"
                >
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-900">
                        {category}
                      </h4>
                    </div>

                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-2">
                        Feedback:
                      </h5>
                      {isEditMode ? (
                        <textarea
                          value={displayData?.feedback?.[category] || ""}
                          onChange={(e) =>
                            handleFeedbackChange(category, e.target.value)
                          }
                          className="w-full text-sm text-gray-600 leading-relaxed border border-gray-300 rounded p-2 bg-white"
                          rows="4"
                        />
                      ) : (
                        <p className="text-sm text-gray-600 leading-relaxed text-justify">
                          {displayData?.feedback?.[category] ||
                            "No feedback available"}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-900">
                        {category}
                      </h4>
                      <span className="text-xs text-gray-500">Analysis</span>
                    </div>
                    <div>
                      <h5 className="text-sm font-medium text-gray-700 mb-2">
                        Analysis:
                      </h5>
                      {isEditMode ? (
                        <textarea
                          value={displayData?.analysis?.[category] || ""}
                          onChange={(e) =>
                            handleAnalysisChange(category, e.target.value)
                          }
                          className="w-full text-sm text-gray-600 leading-relaxed border border-blue-300 rounded p-2 bg-blue-50"
                          rows="4"
                        />
                      ) : (
                        <p className="text-sm text-gray-600 leading-relaxed text-justify">
                          {displayData?.analysis?.[category] ||
                            "No analysis available"}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-900">
                        Improvement Example
                      </h4>
                      <span className="text-xs text-gray-500">Improvement</span>
                    </div>
                    {isEditMode ? (
                      <textarea
                        value={displayData?.re_write_example?.[category] || ""}
                        onChange={(e) =>
                          handleRewriteExampleChange(category, e.target.value)
                        }
                        className="w-full text-sm text-gray-700 italic leading-relaxed border border-green-300 rounded p-3 bg-green-50"
                        rows="3"
                      />
                    ) : (
                      <div className="bg-green-50 border border-green-200 rounded p-3">
                        <p className="text-sm text-gray-700  leading-relaxed text-justify">
                          "
                          {displayData?.re_write_example?.[category] ||
                            "No improvement example available"}
                          "
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {displayData?.irac_breakdown && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                IRAC Breakdown
              </h3>
              <div
                className={`flex flex-wrap gap-4 ${
                  displayData?.irac_breakdown?.issues && Object.keys(displayData.irac_breakdown.issues).length === 1
                    ? "justify-center"
                    : displayData?.irac_breakdown?.issues && Object.keys(displayData.irac_breakdown.issues).length === 2
                    ? "justify-between"
                    : "justify-center"
                }`}
              >
                {displayData?.irac_breakdown?.issues && Object.entries(displayData.irac_breakdown.issues).map(
                  ([issueKey, issueData]) => {
                    const percentage = issueData.issue_percentage || 0;
                    const colorInfo = getScoreColor(percentage);

                    return (
                      <div
                        key={issueKey}
                        className="bg-white border border-gray-200 rounded-lg p-4 w-80 flex-shrink-0"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold text-gray-900">
                            {issueData.issue_name}
                          </h4>
                          <div className="text-right">
                            <div
                              className="text-lg font-bold"
                              style={{ color: colorInfo.color }}
                            >
                              {issueData.issue_total_score}
                            </div>
                            <div
                              className="text-xs"
                              style={{ color: colorInfo.color }}
                            >
                              {percentage.toFixed(1)}%
                            </div>
                          </div>
                        </div>

                        <div className="space-y-3">
                          {["Issue", "Rule", "Analysis", "Conclusion", "Organization", "Writing Style"].map(
                            (component) => {
                              const compData = issueData[component];
                              const compPercentage =
                                compData.total_marks > 0
                                  ? (compData.obtained_marks /
                                      compData.total_marks) *
                                    100
                                  : 0;
                              const compColor = getScoreColor(compPercentage);

                              return (
                                <div
                                  key={component}
                                  className="border-l-2 border-gray-200 pl-3"
                                >
                                  <div className="flex items-center justify-between text-sm mb-1">
                                    <span className="font-medium text-gray-700">
                                      {component}:
                                    </span>
                                    <div className="flex items-center space-x-2">
                                      {isEditMode ? (
                                        <div className="flex items-center space-x-1">
                                          <input
                                            type="number"
                                            min="0"
                                            max={compData.total_marks}
                                            value={
                                              compData.obtained_marks === 0
                                                ? ""
                                                : compData.obtained_marks
                                            }
                                            onChange={(e) =>
                                              handleIRACScoreChange(
                                                issueKey,
                                                component,
                                                e.target.value
                                              )
                                            }
                                            onFocus={(e) => {
                                              if (e.target.value === "0") {
                                                e.target.value = "";
                                              }
                                            }}
                                            onBlur={(e) => {
                                              if (e.target.value === "") {
                                                e.target.value = "0";
                                                handleIRACScoreChange(
                                                  issueKey,
                                                  component,
                                                  "0"
                                                );
                                              }
                                            }}
                                            placeholder="0"
                                            className="w-12 text-xs font-bold text-gray-700 border border-gray-300 rounded px-1 py-0.5 text-center"
                                          />
                                          <span className="text-xs text-gray-500">
                                            /
                                          </span>
                                          <input
                                            type="number"
                                            min="0"
                                            value={
                                              compData?.total_marks === 0
                                                ? ""
                                                : compData?.total_marks
                                            }
                                            onChange={(e) =>
                                              handleIRACMaxScoreChange(
                                                issueKey,
                                                component,
                                                e.target.value
                                              )
                                            }
                                            onFocus={(e) => {
                                              if (e.target.value === "0") {
                                                e.target.value = "";
                                              }
                                            }}
                                            onBlur={(e) => {
                                              if (e.target.value === "") {
                                                e.target.value = "0";
                                                handleIRACMaxScoreChange(
                                                  issueKey,
                                                  component,
                                                  "0"
                                                );
                                              }
                                            }}
                                            placeholder="0"
                                            className="w-12 text-xs font-bold text-gray-700 border border-gray-300 rounded px-1 py-0.5 text-center"
                                          />
                                        </div>
                                      ) : (
                                        <>
                                          <span
                                            style={{ color: compColor.color }}
                                          >
                                            {compData.obtained_marks}/
                                            {compData.total_marks}
                                          </span>
                                          <div
                                            className="w-2 h-2 rounded-full"
                                            style={{
                                              backgroundColor: compColor.color,
                                            }}
                                          ></div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                  {isEditMode ? (
                                    <textarea
                                      value={compData.feedback}
                                      onChange={(e) =>
                                        handleIRACFeedbackChange(
                                          issueKey,
                                          component,
                                          e.target.value
                                        )
                                      }
                                      className="w-full text-xs text-gray-600 leading-relaxed border border-gray-300 rounded p-1 bg-white"
                                      rows="2"
                                    />
                                  ) : (
                                    <p className="text-xs text-gray-600 leading-relaxed">
                                      {compData.feedback}
                                    </p>
                                  )}
                                </div>
                              );
                            }
                          )}
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            </div>
          )}
          {displayData?.missing_topics_analysis && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Missing Topics Analysis
              </h3>
              <div className="flex flex-col lg:flex-row gap-6 justify-around">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-2 text-left">
                  Missed Topics:
                  </h4>
                  {isEditMode ? (
                    <textarea
                      value={
                        displayData?.missing_topics_analysis?.topics_from_model_student_missed?.join(
                          "\n"
                        ) || ""
                      }
                      onChange={(e) =>
                        handleMissingTopicsChange(
                          "topics_from_model_student_missed",
                          e.target.value
                        )
                      }
                      className="w-full text-gray-700 border border-orange-300 rounded p-2 bg-white text-left"
                      rows="4"
                      placeholder="Enter each topic on a new line"
                    />
                  ) : (
                    <ul className="space-y-1">
                      {displayData?.missing_topics_analysis?.topics_from_model_student_missed?.map(
                        (topic, index) => (
                          <li
                            key={index}
                            className="flex items-start justify-start"
                          >
                            <span className="text-orange-500 mr-2 -mt-1">
                              •
                            </span>
                            <span className="text-gray-700 text-left">
                              {topic}
                            </span>
                          </li>
                        )
                      )}
                    </ul>
                  )}
                </div>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900 mb-2 text-left">
                    Specific Missing Content:
                  </h4>
                  {isEditMode ? (
                    <textarea
                      value={
                        displayData?.missing_topics_analysis?.specific_missing_content?.join(
                          "\n"
                        ) || ""
                      }
                      onChange={(e) =>
                        handleMissingTopicsChange(
                          "specific_missing_content",
                          e.target.value
                        )
                      }
                      className="w-full text-gray-700 border border-orange-300 rounded p-2 bg-white text-left"
                      rows="4"
                      placeholder="Enter each missing content item on a new line"
                    />
                  ) : (
                    <ul className="space-y-1">
                      {displayData?.missing_topics_analysis?.specific_missing_content?.map(
                        (content, index) => (
                          <li
                            key={index}
                            className="flex items-start justify-start"
                          >
                            <span className="text-orange-500 mr-2 -mt-1">
                              •
                            </span>
                            <span className="text-gray-700 text-left">
                              {content}
                            </span>
                          </li>
                        )
                      )}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">
              Missed Issues
            </h3>
            {isEditMode ? (
              <textarea
                value={displayData?.missed_issues?.join("\n") || ""}
                onChange={(e) => handleMissedIssuesChange(e.target.value)}
                className="w-full text-gray-700 border border-red-300 rounded p-2 bg-white"
                rows="4"
                placeholder="Enter each missed issue on a new line"
              />
            ) : (
              <ul className="space-y-2">
                {displayData?.missed_issues?.map((issue, index) => (
                  <li key={index} className="block text-left items-left">
                    <span className="text-red-500 mr-2 -mt-1">•</span>
                    <span className="text-gray-700">{issue}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              className="px-4 py-2 text-white rounded-md transition-colors"
              style={{ backgroundColor: theme.Colors.primary }}
            >
              Download Report
            </button>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default ResultDetails;
